import { Card, CardContent as CardContentUI } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ArrowLeft, Save, Search } from "lucide-react";
import { HeaderPage } from "@/components/HeaderPage";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { motion } from "framer-motion";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import toast from "react-hot-toast";
import {
  createTeamSchema,
  type CreateTeamFormData,
} from "@/schemas/teamSchemas";
import { useCreateTeam } from "@/hooks/useTeams";
import { useGetUsers } from "@/hooks/useUsers";
import { useGetCategories } from "@/hooks/useCategories";
import { useGetSectors } from "@/hooks/useSectors";
import { useDebounce } from "@/hooks/useDebounce";
import { MultiSelectTable } from "@/components/MultiSelectTable";

const workShiftOptions = [
  { value: "MORNING", label: "Manhã" },
  { value: "AFTERNOON", label: "Tarde" },
  { value: "NIGHT", label: "Noite" },
  { value: "FULL_TIME", label: "Integral" },
];

const statusOptions = [
  { value: "ACTIVE", label: "Ativa" },
  { value: "INACTIVE", label: "Inativa" },
];

export const CreateTeam = () => {
  const navigate = useNavigate();
  const createTeam = useCreateTeam();
  const { data: categoriesData } = useGetCategories();
  const { data: sectorsData } = useGetSectors();

  const [searchSector, setSearchSector] = useState("");
  const [searchLeader, setSearchLeader] = useState("");
  const [searchMembers, setSearchMembers] = useState("");
  
  const debouncedSearchLeader = useDebounce(searchLeader, 500);
  const debouncedSearchMembers = useDebounce(searchMembers, 500);
  
  // Buscar usuários para líder (todas roles exceto CitizenProfile)
  const { data: leadersData } = useGetUsers({ 
    role: "ADMIN,AGENT,TRIAGE,AUDIT",
    search: debouncedSearchLeader,
    limit: 50
  });
  
  // Buscar usuários para membros (todas roles exceto CitizenProfile)
  const { data: membersData } = useGetUsers({ 
    role: "ADMIN,AGENT,TRIAGE,AUDIT",
    search: debouncedSearchMembers,
    limit: 50
  });

  const {
    handleSubmit,
    register,
    control,
    watch,
    setValue,
    formState: { errors },
  } = useForm<CreateTeamFormData>({
    resolver: zodResolver(createTeamSchema),
    defaultValues: {
      status: "ACTIVE",
      memberIds: [],
      categoryIds: [],
    },
  });

  const memberIds = watch("memberIds");
  const categoryIds = watch("categoryIds");

  const filteredSectors = sectorsData?.data?.filter((sector: any) =>
    sector.name?.toLowerCase().includes(searchSector.toLowerCase()),
  );

  const onSubmit = async (data: CreateTeamFormData) => {
    try {
      await createTeam.mutateAsync(data, {
        onSuccess: () => {
          navigate("/teams");
          toast.success("Equipe criada com sucesso!");
        },
      });
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message || "Erro ao criar equipe";
      toast.error(errorMessage);
    }
  };

  const handleAddMember = (memberId: string) => {
    setValue("memberIds", [...memberIds, memberId]);
  };

  const handleRemoveMember = (memberId: string) => {
    setValue(
      "memberIds",
      memberIds.filter((id) => id !== memberId),
    );
  };

  const handleAddCategory = (categoryId: string) => {
    setValue("categoryIds", [...categoryIds, categoryId]);
  };

  const handleRemoveCategory = (categoryId: string) => {
    setValue(
      "categoryIds",
      categoryIds.filter((id) => id !== categoryId),
    );
  };

  return (
    <div className="flex flex-col h-full bg-gray-50">
      <HeaderPage
        title="Criar Equipe"
        description="Informe os dados da equipe"
      />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex-1 overflow-auto mt-6"
      >
        <form onSubmit={handleSubmit(onSubmit)} className="mx-auto">
          <Card className="bg-white">
            <CardContentUI className="p-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Nome */}
                <div className="space-y-1.5">
                  <label className="text-sm font-medium">
                    Nome da Equipe <span className="text-red-500">*</span>
                  </label>
                  <Input
                    placeholder="Digite o nome da equipe"
                    {...register("name")}
                  />
                  {errors.name && (
                    <p className="text-sm text-red-500">
                      {errors.name.message}
                    </p>
                  )}
                </div>

                {/* Setor */}
                <Controller
                  name="sectorId"
                  control={control}
                  render={({ field }) => (
                    <div className="space-y-2">
                      <label className="text-sm font-medium">
                        Setor <span className="text-red-500">*</span>
                      </label>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione o setor" />
                        </SelectTrigger>
                        <SelectContent>
                          <div className="flex items-center border-b px-3 pb-2">
                            <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
                            <Input
                              placeholder="Pesquisar setor..."
                              value={searchSector}
                              onChange={(e) => setSearchSector(e.target.value)}
                              className="h-8 border-0 focus-visible:ring-0 focus-visible:ring-offset-0"
                            />
                          </div>
                          <div className="max-h-[200px] overflow-y-auto">
                            {filteredSectors?.map((sector: any) => (
                              <SelectItem key={sector.id} value={sector.id}>
                                {sector.name}
                              </SelectItem>
                            ))}
                            {filteredSectors?.length === 0 && (
                              <div className="py-6 text-center text-sm text-gray-500">
                                Nenhum setor encontrado
                              </div>
                            )}
                          </div>
                        </SelectContent>
                      </Select>
                      {errors.sectorId && (
                        <p className="text-sm text-red-500">
                          {errors.sectorId.message}
                        </p>
                      )}
                    </div>
                  )}
                />

                {/* Regional */}
                <div className="space-y-2">
                  <label className="text-sm font-medium">
                    Regional <span className="text-red-500">*</span>
                  </label>
                  <Input
                    placeholder="Digite a regional"
                    {...register("regional")}
                  />
                  {errors.regional && (
                    <p className="text-sm text-red-500">
                      {errors.regional.message}
                    </p>
                  )}
                </div>

                {/* Líder */}
                <Controller
                  name="leaderId"
                  control={control}
                  render={({ field }) => (
                    <div className="space-y-2">
                      <label className="text-sm font-medium">
                        Líder <span className="text-red-500">*</span>
                      </label>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione o líder" />
                        </SelectTrigger>
                        <SelectContent>
                          <div className="flex items-center border-b px-3 pb-2">
                            <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
                            <Input
                              placeholder="Pesquisar líder..."
                              value={searchLeader}
                              onChange={(e) => setSearchLeader(e.target.value)}
                              className="h-8 border-0 focus-visible:ring-0 focus-visible:ring-offset-0"
                            />
                          </div>
                          <div className="max-h-[200px] overflow-y-auto">
                            {leadersData?.data?.map((user: any) => (
                              <SelectItem key={user.id} value={user.id}>
                                <div className="flex flex-col">
                                  <span>{user.profile?.fullName || user.email}</span>
                                  <span className="text-xs text-gray-500">{user.role}</span>
                                </div>
                              </SelectItem>
                            ))}
                            {leadersData?.data?.length === 0 && (
                              <div className="py-6 text-center text-sm text-gray-500">
                                {searchLeader ? "Nenhum líder encontrado" : "Digite para pesquisar..."}
                              </div>
                            )}
                          </div>
                        </SelectContent>
                      </Select>
                      {errors.leaderId && (
                        <p className="text-sm text-red-500">
                          {errors.leaderId.message}
                        </p>
                      )}
                    </div>
                  )}
                />

                {/* Turno de Trabalho */}
                <Controller
                  name="workShift"
                  control={control}
                  render={({ field }) => (
                    <div className="space-y-2">
                      <label className="text-sm font-medium">
                        Turno de Trabalho{" "}
                        <span className="text-red-500">*</span>
                      </label>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione o turno" />
                        </SelectTrigger>
                        <SelectContent>
                          {workShiftOptions.map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {errors.workShift && (
                        <p className="text-sm text-red-500">
                          {errors.workShift.message}
                        </p>
                      )}
                    </div>
                  )}
                />

                {/* Status */}
                <Controller
                  name="status"
                  control={control}
                  render={({ field }) => (
                    <div className="space-y-2">
                      <label className="text-sm font-medium">
                        Status <span className="text-red-500">*</span>
                      </label>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione o status" />
                        </SelectTrigger>
                        <SelectContent>
                          {statusOptions.map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {errors.status && (
                        <p className="text-sm text-red-500">
                          {errors.status.message}
                        </p>
                      )}
                    </div>
                  )}
                />
              </div>

              <div className="mt-6">
                <div className="space-y-3">
                  <label className="text-sm font-medium">Membros da Equipe</label>
                  <div className="flex items-center gap-2 mb-3">
                    <div className="relative flex-1">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <Input
                        placeholder="Pesquisar membros por nome ou email..."
                        value={searchMembers}
                        onChange={(e) => setSearchMembers(e.target.value)}
                        className="pl-9"
                      />
                    </div>
                  </div>
                  <MultiSelectTable
                    title=""
                    items={membersData?.data || []}
                    selectedIds={memberIds}
                    onAdd={handleAddMember}
                    onRemove={handleRemoveMember}
                    searchPlaceholder=""
                    emptyMessage="Nenhum membro adicionado"
                    displayField={(user) => `${user.profile?.fullName || user.email} (${user.role})`}
                  />
                </div>
              </div>

              {/* Categorias */}
              <div className="mt-6">
                <MultiSelectTable
                  title="Categorias"
                  items={categoriesData?.data || []}
                  selectedIds={categoryIds}
                  onAdd={handleAddCategory}
                  onRemove={handleRemoveCategory}
                  searchPlaceholder="Pesquisar categorias..."
                  emptyMessage="Nenhuma categoria adicionada"
                  displayField={(category) => category.name}
                />
              </div>

              {/* Botões */}
              <div className="flex gap-3 mt-6 pt-6 border-t">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate("/teams")}
                  className="flex-1"
                >
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  className="flex-1 text-white"
                  disabled={createTeam.isPending}
                >
                  <Save className="mr-2 h-4 w-4" />
                  {createTeam.isPending ? "Salvando..." : "Salvar"}
                </Button>
              </div>
            </CardContentUI>
          </Card>
        </form>
      </motion.div>
    </div>
  );
};
